import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BookCover } from "../components/BookCover";
import { StarDisplay, StarRating } from "../components/StarRating";
import { toast } from "sonner";
import { ArrowLeft, Trash2, ExternalLink, Heart, Share2, Quote, Image as ImageIcon, X, MessageCircle, Tag, Plus, Book, Library, Pencil } from "lucide-react";
import { SeriesAddModal } from "../components/SeriesAddModal";
import { EditSeriesModal } from "../components/EditSeriesModal";
import { ShareImageCard } from "../components/ShareImageCard";

// Map browser language / timezone → local Amazon TLD so the reader sees
// their own regional store first in Where-to-buy. Falls back to .com.
function detectAmazonHost() {
  try {
    if (typeof navigator === "undefined") return "www.amazon.com";
    const lang = (navigator.language || "en-US").toLowerCase();
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    // Language > country signal is stronger than TZ, so check language first.
    const map = [
      { match: /^en-gb|^cy/, host: "www.amazon.co.uk" },
      { match: /^en-au/, host: "www.amazon.com.au" },
      { match: /^en-ca|^fr-ca/, host: "www.amazon.ca" },
      { match: /^de|^gsw|^de-at|^de-ch/, host: "www.amazon.de" },
      { match: /^fr|^fr-fr|^fr-be|^oc/, host: "www.amazon.fr" },
      { match: /^es-mx/, host: "www.amazon.com.mx" },
      { match: /^es/, host: "www.amazon.es" },
      { match: /^it/, host: "www.amazon.it" },
      { match: /^nl/, host: "www.amazon.nl" },
      { match: /^pl/, host: "www.amazon.pl" },
      { match: /^sv|^sv-se/, host: "www.amazon.se" },
      { match: /^pt-br/, host: "www.amazon.com.br" },
      { match: /^ja/, host: "www.amazon.co.jp" },
      { match: /^ar|^he/, host: "www.amazon.ae" },
      { match: /^tr/, host: "www.amazon.com.tr" },
      { match: /^hi|^bn|^ta|^en-in/, host: "www.amazon.in" },
    ];
    const byLang = map.find((m) => m.match.test(lang));
    if (byLang) return byLang.host;
    // Timezone fallback for common regions.
    if (/^Europe\/London/.test(tz)) return "www.amazon.co.uk";
    if (/^Europe\/(Berlin|Vienna|Zurich)/.test(tz)) return "www.amazon.de";
    if (/^Europe\/Paris/.test(tz)) return "www.amazon.fr";
    if (/^Europe\/Madrid/.test(tz)) return "www.amazon.es";
    if (/^Europe\/Rome/.test(tz)) return "www.amazon.it";
    if (/^Australia\//.test(tz)) return "www.amazon.com.au";
    return "www.amazon.com";
  } catch {
    return "www.amazon.com";
  }
}

const HUBS = (t, a) => {
  const q = encodeURIComponent(`${t} ${a}`);
  const amazonHost = detectAmazonHost();
  const amazonLabel = amazonHost === "www.amazon.com" ? "Amazon" : `Amazon (${amazonHost.replace(/^www\./, "").replace(/^amazon\./, "")})`;
  return [
    // The reader's local Amazon leads the list — a UK user sees .co.uk first,
    // a German user sees .de, etc. Falls back to .com for unknown locales.
    { name: amazonLabel, url: `https://${amazonHost}/s?k=${q}` },
    { name: "World of Books", url: `https://www.worldofbooks.com/en-gb/search?q=${q}` },
    { name: "AliExpress", url: `https://www.aliexpress.com/wholesale?SearchText=${q}` },
    { name: "Bookshop.org", url: `https://bookshop.org/search?keywords=${q}` },
    { name: "Vinted", url: `https://www.vinted.com/catalog?search_text=${q}&catalog[]=1185` },
    { name: "Waterstones", url: `https://www.waterstones.com/books/search/term/${q}` },
  ];
};

export default function BookDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [myRating, setMyRating] = useState(0);
  const [myText, setMyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [critics, setCritics] = useState("");
  const [price, setPrice] = useState(null);
  const [editions, setEditions] = useState([]);
  const [editionsLoading, setEditionsLoading] = useState(false);
  const [showCoverPicker, setShowCoverPicker] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showAddToList, setShowAddToList] = useState(false);
  const [showSeriesAdd, setShowSeriesAdd] = useState(false);
  const [showEditSeries, setShowEditSeries] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);
  const [mySeriesNames, setMySeriesNames] = useState([]);
  const [myLists, setMyLists] = useState([]);
  const [newListName, setNewListName] = useState("");
  const [savingList, setSavingList] = useState("");

  const load = () => {
    api.get(`/books/${id}`).then(({ data }) => setBook(data)).catch(() => setBook(false));
    api.get(`/books/${id}/reviews`).then(({ data }) => {
      setReviews(data);
      if (user && typeof user === "object") {
        const mine = data.find((r) => r.user_id === user.id);
        if (mine) { setMyRating(mine.rating); setMyText(mine.text || ""); }
      }
    });
    api.get(`/books/${id}/critics`).then(({ data }) => setCritics(data.consensus || ""));
    api.get(`/books/${id}/price`).then(({ data }) => setPrice(data));
  };
  useEffect(load, [id, user]);

  // Always pre-load editions for the Editions panel + cover picker.
  useEffect(() => {
    if (!book?.openlibrary_key || !book.openlibrary_key.startsWith("/works/")) return;
    setEditionsLoading(true);
    fetch(`https://openlibrary.org${book.openlibrary_key}/editions.json?limit=40`)
      .then((r) => r.json())
      .then((d) => setEditions(d.entries || []))
      .catch(() => setEditions([]))
      .finally(() => setEditionsLoading(false));
  }, [book?.openlibrary_key]);

  // Load the viewer's lists (only when the modal opens, for freshness).
  useEffect(() => {
    if (!showAddToList) return;
    api.get("/sections").then(({ data }) => setMyLists(data || [])).catch(() => setMyLists([]));
    api.get("/me/library").then(({ data }) => {
      const items = Array.isArray(data) ? data : (data?.items || []);
      const set = new Set();
      for (const it of items) {
        const s = (it.book?.series_name || "").trim();
        if (s) set.add(s);
      }
      setMySeriesNames(Array.from(set).sort((a, b) => a.localeCompare(b)));
    }).catch(() => setMySeriesNames([]));
  }, [showAddToList]);

  const addBookToList = async (listId) => {
    setSavingList(listId);
    try {
      await api.post(`/sections/${listId}/books`, { book_id: id });
      toast.success("Added to list");
      setShowAddToList(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingList("");
    }
  };

  const createListAndAdd = async (e) => {
    e.preventDefault();
    const n = newListName.trim();
    if (!n) return;
    setSavingList("__new__");
    try {
      const { data: list } = await api.post("/sections", { name: n });
      await api.post(`/sections/${list.id}/books`, { book_id: id });
      toast.success(`Added to "${n}"`);
      setNewListName("");
      setShowAddToList(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingList("");
    }
  };

  const applyCustomCover = async () => {
    const url = window.prompt(
      "Paste a direct image URL for your cover (must start with https://):",
      ""
    );
    if (!url) return;
    const trimmed = url.trim();
    if (!/^https:\/\//i.test(trimmed)) {
      toast.error("URL must start with https://");
      return;
    }
    try {
      const { data } = await api.patch(`/books/${id}`, { cover_url: trimmed });
      setBook(data);
      toast.success("Custom cover applied");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };


  const applyEdition = async (edition) => {
    // Merge the edition's metadata onto the book.
    const updates = {};
    if (edition.covers && edition.covers[0] > 0) {
      updates.cover_url = `https://covers.openlibrary.org/b/id/${edition.covers[0]}-L.jpg`;
    }
    const editionYear = parseInt((edition.publish_date || "").match(/\d{4}/)?.[0] || "", 10);
    if (editionYear) updates.year = editionYear;
    if (edition.number_of_pages) updates.pages = edition.number_of_pages;
    if (!Object.keys(updates).length) {
      toast.error("This edition has no extra info to apply");
      return;
    }
    try {
      // Backend only accepts cover_url in PATCH today — try the full update,
      // fall back to cover-only so older deployments still work.
      try {
        const { data } = await api.patch(`/books/${id}`, updates);
        setBook(data);
      } catch {
        const { data } = await api.patch(`/books/${id}`, { cover_url: updates.cover_url });
        setBook(data);
      }
      toast.success("Edition applied");
      setShowCoverPicker(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!myRating) { toast.error("Please choose a star rating"); return; }
    setSubmitting(true);
    try {
      await api.post(`/books/${id}/reviews`, { rating: myRating, text: myText });
      toast.success("Review saved"); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const deleteBook = async () => {
    if (!confirm("Delete this book?")) return;
    try { await api.delete(`/books/${id}`); toast.success("Book removed"); navigate("/discover"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const addToWishlist = async () => {
    try { await api.post("/me/wishlist", { book_id: id }); toast.success("Added to wish-list"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (book === null) return <div className="p-12 font-mono uppercase tracking-widest text-xs text-[#8C7F65]">Loading…</div>;
  if (book === false) return <div className="p-12 font-heading text-2xl text-[#4A4030]">Book not found.</div>;

  const isOwner = user && typeof user === "object" && (book.added_by === user.id || user.role === "admin");
  const hubs = HUBS(book.title, book.author);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 fade-up" data-testid="book-detail-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] text-[#4A4030] hover:text-[#0A0A0A] mb-8" data-testid="back-link">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back
      </button>

      <div className="grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-4">
          <BookCover src={book.cover_url} title={book.title} author={book.author} />
          {book.openlibrary_key && book.openlibrary_key.startsWith("/works/") && (
            <button onClick={() => setShowCoverPicker(true)} className="mt-3 w-full inline-flex items-center justify-center gap-1.5 font-mono uppercase tracking-widest text-[10px] text-[#4A4030] hover:text-[#0A0A0A]" data-testid="open-cover-picker">
              <ImageIcon className="w-3 h-3" strokeWidth={1.5} /> Choose a different edition
            </button>
          )}
          <dl className="mt-6 space-y-2 font-mono text-[10px] uppercase tracking-widest text-[#4A4030]">
            <Row label="Genre" value={book.genre} />
            <Row label="Age" value={book.age_rating} />
            {book.year && <Row label="Year" value={book.year} />}
            {book.pages && <Row label="Pages" value={book.pages} />}
            {price && <Row label="Avg price" value={`€${price.average} (€${price.low}–€${price.high})`} testId="book-price" />}
            <Row label="Added by" value={book.added_by_name} />
          </dl>
          <div className="mt-6 space-y-2">
            {user && typeof user === "object" && (
              <>
                <button onClick={addToWishlist} className="w-full inline-flex items-center justify-center gap-2 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[11px] px-4 py-2.5 rounded-sm transition-colors" data-testid="add-to-wishlist">
                  <Heart className="w-3.5 h-3.5" strokeWidth={1.5} /> Add to wish-list
                </button>
                <button onClick={() => setShowAddToList(true)} className="w-full inline-flex items-center justify-center gap-2 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[11px] px-4 py-2.5 rounded-sm transition-colors" data-testid="add-to-list">
                  <Tag className="w-3.5 h-3.5" strokeWidth={1.5} /> Add to list
                </button>
                <button onClick={() => setShowShare(true)} className="w-full inline-flex items-center justify-center gap-2 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[11px] px-4 py-2.5 rounded-sm transition-colors" data-testid="share-book">
                  <Share2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Share with a friend
                </button>
              </>
            )}
            {isOwner && (
              <button onClick={deleteBook} className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-mono uppercase tracking-widest text-[#7C1D1D] hover:underline mt-2" data-testid="delete-book-button">
                <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Delete book
              </button>
            )}
          </div>
        </div>

        <div className="md:col-span-8 space-y-10">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030] flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>{book.genre}</span>
              {book.series_name ? (
                <>
                  <Link
                    to={`/series/${encodeURIComponent(book.series_name)}`}
                    className="text-[#525252] hover:text-[#0A0A0A] hover:underline"
                    data-testid="book-series"
                    title={`See all books in "${book.series_name}"`}
                  >
                    · {book.series_name}{book.series_index != null ? ` #${book.series_index}` : ""}
                  </Link>
                  {isOwner && (
                    <button
                      onClick={() => setShowEditSeries(true)}
                      className="inline-flex items-center gap-1 text-[#525252] hover:text-[#0A0A0A] p-1 -m-1"
                      aria-label="Edit series"
                      data-testid="edit-series-button"
                    >
                      <Pencil className="w-3 h-3" strokeWidth={1.5} />
                    </button>
                  )}
                </>
              ) : (
                isOwner && (
                  <button
                    onClick={() => setShowEditSeries(true)}
                    className="inline-flex items-center gap-1 text-[#525252] hover:text-[#0A0A0A] underline underline-offset-2 normal-case tracking-normal"
                    data-testid="add-to-series-button"
                  >
                    <Plus className="w-3 h-3" strokeWidth={1.5} /> Add to series
                  </button>
                )
              )}
            </div>
            <h1 className="font-heading text-4xl md:text-6xl text-[#0A0A0A] mt-3 leading-[1.0] tracking-tight" data-testid="book-title">{book.title}</h1>
            <div className="font-heading text-xl text-[#4A4030] mt-2" data-testid="book-author">by {book.author}</div>
            {user && typeof user === "object" && (
              <button
                onClick={() => setShowSeriesAdd(true)}
                className="mt-4 inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[11px] px-3 py-1.5 rounded-sm transition-colors"
                data-testid="add-whole-series-from-detail"
              >
                <Library className="w-3.5 h-3.5" strokeWidth={1.5} /> Add the whole series
              </button>
            )}
            <div className="flex items-center gap-3 mt-5">
              <StarDisplay value={book.avg_rating} size={18} />
              <span className="font-mono text-xs text-[#4A4030]" data-testid="book-rating-summary">
                {book.avg_rating.toFixed(1)} · {book.review_count} {book.review_count === 1 ? "review" : "reviews"}
              </span>
            </div>
          </div>

          {critics && (
            <div className="border-l-2 border-[#0A0A0A] pl-5 py-2" data-testid="critics-consensus">
              <div className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030] mb-2 inline-flex items-center gap-2"><Quote className="w-3 h-3" strokeWidth={1.5} /> Critics' consensus</div>
              <p className="font-heading text-lg md:text-xl text-[#0A0A0A] italic leading-snug">"{critics}"</p>
            </div>
          )}

          {book.description && <p className="text-[#0A0A0A] text-base md:text-lg leading-relaxed max-w-2xl" data-testid="book-description">{book.description}</p>}

          {/* Buying hubs */}
          <div className="border-t border-[rgba(10,10,10,0.08)] pt-6">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030]">Where to buy</div>
              <button
                type="button"
                onClick={() => setShowShareCard(true)}
                className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#F2EAD3] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-3 py-2 rounded-sm transition-colors"
                data-testid="share-card-btn"
                title="Make a share card"
              >
                <Share2 className="w-3 h-3" strokeWidth={1.5} /> Share card
              </button>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="selling-hubs">
              {hubs.map((h) => (
                <a key={h.name} href={h.url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-3 py-2 rounded-sm transition-colors"
                   data-testid={`hub-${h.name.toLowerCase().replace(/[^a-z]/g,'-')}`}>
                  {h.name} <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>

          {/* Editions panel */}
          {book.openlibrary_key && book.openlibrary_key.startsWith("/works/") && (
            <div className="border-t border-[rgba(10,10,10,0.08)] pt-6" data-testid="editions-panel">
              <div className="flex items-end justify-between gap-4 mb-4">
                <div>
                  <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030]">Editions</div>
                  <p className="text-sm text-[#525252] mt-2 max-w-xl">All known editions of this book — paperback, hardback, audio, foreign-language. Tap one to swap the cover and metadata on your shelf.</p>
                </div>
                {!editionsLoading && editions.length > 0 && (
                  <span className="font-mono uppercase tracking-widest text-[10px] text-[#8C7F65] whitespace-nowrap">{editions.length} editions</span>
                )}
              </div>
              {editionsLoading ? (
                <div className="font-mono uppercase tracking-widest text-xs text-[#A3A3A3]">Loading editions…</div>
              ) : editions.length === 0 ? (
                <div className="text-sm text-[#525252]">No additional editions found on Open Library.</div>
              ) : (
                <ul className="grid sm:grid-cols-2 gap-px bg-[rgba(10,10,10,0.08)] border border-[rgba(10,10,10,0.08)]" data-testid="editions-list">
                  {editions.slice(0, 8).map((e) => {
                    const coverId = e.covers && e.covers.find((c) => c > 0);
                    const coverUrl = coverId ? `https://covers.openlibrary.org/b/id/${coverId}-M.jpg` : "";
                    const publisher = (e.publishers && e.publishers[0]) || "—";
                    const lang = e.languages && e.languages[0]?.key?.replace("/languages/", "").toUpperCase();
                    const isbn = (e.isbn_13 && e.isbn_13[0]) || (e.isbn_10 && e.isbn_10[0]) || "";
                    const isCurrent = book.cover_url === `https://covers.openlibrary.org/b/id/${coverId}-L.jpg`;
                    return (
                      <li key={e.key} className="bg-[#FAF5E6] p-4 flex gap-4" data-testid={`edition-row-${e.key.replace(/\W/g,'_')}`}>
                        <div className="w-16 flex-shrink-0 aspect-[2/3] bg-[#E8DFC1] border border-[rgba(10,10,10,0.10)] overflow-hidden">
                          {coverUrl && <img src={coverUrl} alt="" className="w-full h-full object-cover" loading="lazy" onError={(ev) => { ev.currentTarget.style.display = "none"; }} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-heading text-sm text-[#0A0A0A] leading-snug line-clamp-2">{e.title || book.title}</div>
                          <dl className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mt-2 space-y-0.5">
                            {e.publish_date && <div>Published · <span className="normal-case text-[#0A0A0A] font-body">{e.publish_date}</span></div>}
                            <div>Publisher · <span className="normal-case text-[#0A0A0A] font-body">{publisher}</span></div>
                            {e.number_of_pages && <div>Pages · <span className="normal-case text-[#0A0A0A] font-body">{e.number_of_pages}</span></div>}
                            {lang && <div>Lang · <span className="normal-case text-[#0A0A0A] font-body">{lang}</span></div>}
                            {isbn && <div>ISBN · <span className="normal-case text-[#0A0A0A] font-body">{isbn}</span></div>}
                          </dl>
                          {isOwner && (
                            <button
                              onClick={() => applyEdition(e)}
                              className="mt-3 inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] text-[#0A0A0A] hover:underline"
                              data-testid={`use-edition-${e.key.replace(/\W/g,'_')}`}
                            >
                              {isCurrent ? "Current edition" : "Use this edition →"}
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                  {isOwner && (
                    <li className="bg-[#FAF5E6] p-4 flex gap-4 items-center" data-testid="edition-row-custom-cover">
                      <div className="w-16 flex-shrink-0 aspect-[2/3] bg-[#E8DFC1] border border-dashed border-[rgba(10,10,10,0.30)] flex items-center justify-center">
                        <ImageIcon className="w-4 h-4 text-[#8C7F65]" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-heading text-sm text-[#0A0A0A] leading-snug">Missing?</div>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mt-2">Add your own cover</p>
                        <button
                          onClick={applyCustomCover}
                          className="mt-3 inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] text-[#0A0A0A] hover:underline"
                          data-testid="add-custom-cover"
                        >
                          <Plus className="w-3 h-3" strokeWidth={1.5} /> Paste an image URL
                        </button>
                      </div>
                    </li>
                  )}
                </ul>
              )}
              {editions.length > 8 && (
                <button onClick={() => setShowCoverPicker(true)} className="mt-4 font-mono uppercase tracking-widest text-[10px] text-[#0A0A0A] hover:underline" data-testid="all-editions">
                  See all {editions.length} editions →
                </button>
              )}
            </div>
          )}

          {/* Reviews */}
          {user && typeof user === "object" && user.role === "sub_account" ? (
            <div className="border-t border-[rgba(10,10,10,0.08)] pt-8 border-l-2 border-l-[#0A0A0A] pl-5" data-testid="review-blocked-sub">
              <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030] mb-1">Reviewing is off in sub-accounts</div>
              <p className="text-sm text-[#0A0A0A]">Public reviews can only be posted from your main account. Switch back from <Link to="/sub-accounts" className="underline">Sub-accounts</Link> to leave one.</p>
            </div>
          ) : user && typeof user === "object" ? (
            <form onSubmit={submitReview} className="border-t border-[rgba(10,10,10,0.08)] pt-8 space-y-4" data-testid="review-form">
              <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030]">Your review</div>
              <StarRating value={myRating} onChange={setMyRating} size={22} testIdPrefix="my-star" />
              <textarea value={myText} onChange={(e) => setMyText(e.target.value)} placeholder="Share your thoughts (optional)…" rows={4}
                className="w-full bg-[#FAF5E6] border border-[rgba(10,10,10,0.12)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                data-testid="review-text-input" />
              <button disabled={submitting} className="bg-[#0A0A0A] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-6 py-3 rounded-sm hover:bg-[#1F1F1F] disabled:opacity-60 transition-colors" data-testid="review-submit-button">
                {submitting ? "Saving…" : "Save review"}
              </button>
            </form>
          ) : (
            <div className="border-t border-[rgba(10,10,10,0.08)] pt-8">
              <Link to="/login" className="font-mono uppercase tracking-widest text-xs text-[#0A0A0A] hover:underline">Sign in to leave a review →</Link>
            </div>
          )}

          <div className="border-t border-[rgba(10,10,10,0.08)] pt-8">
            <h2 className="font-heading text-2xl text-[#0A0A0A] mb-6 tracking-tight">Community reviews</h2>
            {reviews.length === 0 ? (
              <div className="font-heading text-lg text-[#4A4030]">No reviews yet — be the first.</div>
            ) : (
              <ul className="space-y-6">
                {reviews.map((r) => {
                  const canMessage = user && typeof user === "object" && r.user_id !== user.id;
                  return (
                  <li key={r.id} className="border-b border-[rgba(10,10,10,0.06)] pb-6 last:border-0" data-testid={`review-${r.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#E8DFC1] border border-[rgba(10,10,10,0.08)] flex items-center justify-center font-heading text-sm text-[#0A0A0A]">{r.user_name?.[0]?.toUpperCase() || "?"}</div>
                        <div>
                          <div className="font-heading text-base text-[#0A0A0A]">{r.user_name}</div>
                          <div className="font-mono text-[10px] uppercase tracking-widest text-[#8C7F65]">{new Date(r.created_at).toLocaleDateString()}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <StarDisplay value={r.rating} size={14} />
                        {canMessage && (
                          <button
                            onClick={async () => {
                              try {
                                const { data } = await api.post("/messages/threads/by-user", { user_id: r.user_id });
                                navigate(`/messages/${data.id}`);
                              } catch (err) {
                                toast.error(formatApiError(err));
                              }
                            }}
                            className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-2.5 py-1.5 rounded-sm transition-colors"
                            data-testid={`message-reader-${r.id}`}
                            title={`Message ${r.user_name}`}
                          >
                            <MessageCircle className="w-3 h-3" strokeWidth={1.5} /> Message
                          </button>
                        )}
                      </div>
                    </div>
                    {r.text && <p className="text-[#0A0A0A] mt-3 leading-relaxed text-[15px]">{r.text}</p>}
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* All-editions modal — same data as the inline panel but full list, grid-of-covers */}
      {showCoverPicker && (
        <Modal onClose={() => setShowCoverPicker(false)} title="All editions">
          {editionsLoading || editions.length === 0 ? (
            <div className="font-mono uppercase tracking-widest text-xs text-[#8C7F65] py-10 text-center">Loading editions…</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
              {editions.filter((e) => e.covers && e.covers[0] > 0).map((e) => {
                const url = `https://covers.openlibrary.org/b/id/${e.covers[0]}-L.jpg`;
                return (
                  <button
                    key={e.key}
                    onClick={() => applyEdition(e)}
                    className="block text-left hover:opacity-80 transition-opacity"
                    data-testid={`edition-${e.covers[0]}`}
                  >
                    <img src={url} alt="" className="w-full aspect-[2/3] object-cover border border-[rgba(10,10,10,0.10)]" loading="lazy" />
                    <div className="font-mono uppercase tracking-widest text-[9px] text-[#525252] mt-2 line-clamp-2">
                      {e.publish_date || "—"}{e.publishers ? ` · ${e.publishers[0]}` : ""}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Modal>
      )}

      {/* Add to list modal */}
      {showAddToList && (
        <Modal onClose={() => setShowAddToList(false)} title="Add to a list">
          <form onSubmit={createListAndAdd} className="flex gap-2 mb-6" data-testid="add-to-list-create">
            <input
              value={newListName}
              onChange={(ev) => setNewListName(ev.target.value)}
              placeholder='New list name, e.g. "Beach reads 2026"'
              maxLength={60}
              className="flex-1 bg-[#FAF5E6] border border-[rgba(10,10,10,0.12)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black"
              data-testid="new-list-name-input"
            />
            <button
              type="submit"
              disabled={!newListName.trim() || savingList === "__new__"}
              className="inline-flex items-center gap-1.5 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm disabled:opacity-50 transition-colors"
              data-testid="new-list-create"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Create + add
            </button>
          </form>

          <div className="border-t border-[rgba(10,10,10,0.08)] pt-5">
            <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mb-3">Or pick an existing list</div>
            {myLists.length === 0 ? (
              <div className="text-sm text-[#525252]">You don't have any lists yet — make one above.</div>
            ) : (
              <ul className="space-y-2" data-testid="existing-lists">
                {myLists.map((list) => {
                  const already = (list.book_ids || []).includes(id);
                  return (
                    <li key={list.id}>
                      <button
                        onClick={() => addBookToList(list.id)}
                        disabled={already || savingList === list.id}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-sm border transition-colors text-left ${
                          already
                            ? "bg-[#E8DFC1] border-transparent text-[#525252] cursor-default"
                            : "bg-[#FAF5E6] border-[rgba(10,10,10,0.12)] hover:bg-black/5 text-[#0A0A0A]"
                        }`}
                        data-testid={`add-to-list-${list.id}`}
                      >
                        <span className="font-heading text-base">{list.name}</span>
                        <span className="font-mono uppercase tracking-widest text-[10px]">
                          {already ? "Already in" : `${(list.book_ids || []).length} books`}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Modal>
      )}

      {/* Share modal */}
      {showShare && <ShareModal bookId={id} bookTitle={book.title} onClose={() => setShowShare(false)} />}

      {/* Add-whole-series modal */}
      <SeriesAddModal
        open={showSeriesAdd}
        seedKey={book.openlibrary_key}
        onClose={() => setShowSeriesAdd(false)}
        onAdded={() => setShowSeriesAdd(false)}
      />
      <EditSeriesModal
        open={showEditSeries}
        book={book}
        existingSeries={mySeriesNames}
        onClose={() => setShowEditSeries(false)}
        onSaved={(updated) => {
          setShowEditSeries(false);
          if (updated) {
            setBook((b) => ({ ...(b || {}), ...updated }));
            // If a brand-new series name was created, add it to the dropdown
            // for next time — cheaper than refetching /me/library.
            const newName = (updated.series_name || "").trim();
            if (newName && !mySeriesNames.includes(newName)) {
              setMySeriesNames([...mySeriesNames, newName].sort((a, b) => a.localeCompare(b)));
            }
          } else {
            // Series was removed — refetch to keep the state clean.
            api.get(`/books/${book.id}`).then(({ data }) => setBook(data));
          }
        }}
      />
      {showShareCard && (
        <ShareImageCard
          book={book}
          rating={myRating || book.avg_rating || 0}
          review={myText || ""}
          onClose={() => setShowShareCard(false)}
        />
      )}
    </div>
  );
}

function Row({ label, value, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[rgba(10,10,10,0.06)] pb-2">
      <span className="text-[#8C7F65]">{label}</span>
      <span className="text-[#0A0A0A] normal-case font-body text-sm" data-testid={testId}>{value}</span>
    </div>
  );
}

function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#FAF5E6] border border-[rgba(10,10,10,0.12)] rounded-sm max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-[rgba(10,10,10,0.10)]">
          <div className="font-heading text-xl text-[#0A0A0A] tracking-tight">{title}</div>
          <button onClick={onClose} className="p-1 hover:bg-black/5 rounded-sm" data-testid="modal-close"><X className="w-5 h-5" strokeWidth={1.5} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function ShareModal({ bookId, bookTitle, onClose }) {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState(`I think you'd love "${bookTitle}".`);
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/share/book", { book_id: bookId, recipient_email: email, message: msg });
      toast.success("Sent — they'll receive a note shortly.");
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} title="Share this book">
      <form onSubmit={send} className="space-y-4" data-testid="share-form">
        <label className="block space-y-1.5">
          <span className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030]">Friend's email</span>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                 className="w-full bg-[#F2EAD3] border border-[rgba(10,10,10,0.12)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                 data-testid="share-email" />
        </label>
        <label className="block space-y-1.5">
          <span className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030]">Note</span>
          <textarea rows={4} value={msg} onChange={(e) => setMsg(e.target.value)}
                    className="w-full bg-[#F2EAD3] border border-[rgba(10,10,10,0.12)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                    data-testid="share-message" />
        </label>
        <button type="submit" disabled={busy} className="w-full bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs py-3 rounded-sm disabled:opacity-60 transition-colors" data-testid="share-submit">
          {busy ? "Sending…" : "Send"}
        </button>
      </form>
    </Modal>
  );
}
